import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import Modal from "../../common/Modal/Modal";
import styles from "./ViewRoutingModal.module.scss";
import { useTranslation } from "react-i18next";
import ViewRoutingTable from "./ViewRoutingTable/ViewRoutingTable";
import Button from "../../common/Button/Button";
import {
  Approver,
  useViewRoutingContext,
  ViewRoutingProvider,
} from "./ViewRoutingContext/ViewRoutingContext";
import AddApproverCell from "./AddApproverCell/AddApproverCell";
import {
  AwardApprover,
  HATVAwardRoutingDetails,
  useGetAwardRoutingDetailsQuery,
  useGetUsersListByNativeIDsQuery,
  UserItem,
  useResetAwardRoutingMutation,
  useCreateAwardRoutingMutation,
} from "../../../redux/services/hatvAwardApprovalAPI";
import ViewRoutingLoader from "./ViewRoutingLoader/ViewRoutingLoader";
import { HATV_AWARD_ROUTING_STATUS } from "../../../constants/dataConstants";
import { toastMessageType } from "../../../types/types";

const ViewRoutingModalStyles = {
  minWidth: "660px",
  overflow: "visible",
};

const ViewRoutingModalContainerStyles = {
  padding: 0,
};

interface ViewRoutingModalProps {
  aboDetails: {
    name: string;
    affiliateCode: string;
    aboNumber: string;
    awardCode: string;
    awardName: string;
    awardTagCode: number;
    awardQualifiedMonth: string;
  };
  performanceYear: string;
  routingRoleIndicators: HATVRoutingRoleIndicators;
  setIsStatusUpdate?: (apiStatus: string) => void;
}

const ViewRoutingModal: React.FC<PropsWithChildren<ViewRoutingModalProps>> = ({
  children,
  aboDetails,
  performanceYear,
  routingRoleIndicators,
  setIsStatusUpdate,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [localReset, setLocalReset] = useState(false);

  const { data, isFetching, isError } = useGetAwardRoutingDetailsQuery(
    {
      affiliateCode: aboDetails.affiliateCode,
      aboNumber: aboDetails.aboNumber,
      awardCode: aboDetails.awardCode,
      performanceYear,
      awardQualifiedMonth: aboDetails?.awardQualifiedMonth,
    },
    { skip: !open }
  );

  const [toastConfig, setToastConfig] = useState({
    message: "",
    type: "" as toastMessageType,
    show: false,
  });

  const setShowToast = (show: boolean) =>
    setToastConfig((prev) => ({ ...prev, show }));

  const nativeIDs = data ? getNativeIdsFromRes(data) : [];

  const { data: userDetailsRes, isFetching: isUserListLoading } =
    useGetUsersListByNativeIDsQuery(
      {
        nativeIDs,
      },
      { skip: !nativeIDs.length }
    );

  const [createAwardRouting, createResult] = useCreateAwardRoutingMutation();
  const [resetAwardRouting, resetResult] = useResetAwardRoutingMutation();

  const { approvers, setApprovers, viewOnly, setViewOnly } =
    useViewRoutingContext();

  const resetApproversList = useCallback(() => {
    if (!data) return;
    if (!userDetailsRes?.body) return;
    setApprovers(
      data.award?.awardApprovers
        ? data.award.awardApprovers
            .slice()
            .sort((a, b) => a.approvalSequence - b.approvalSequence)
            .map((approver) =>
              mapAwardApproverToApproverItem(approver, userDetailsRes.body)
            )
        : []
    );
  }, [data, setApprovers, userDetailsRes]);

  useEffect(() => {
    resetApproversList();
  }, [resetApproversList]);

  useEffect(() => {
    if (isError) {
      setToastConfig({
        message: "Failed to fetch Award details. Please try again.",
        type: "error",
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate("fail");
    }
  }, [isError]);

  useEffect(() => {
    if (createResult.isUninitialized || createResult.isLoading) return;
    if (createResult.isError) {
      setToastConfig({
        message: "Failed to route Award Approvers. Please try again.",
        type: "error",
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate("fail");
    }

    if (createResult.isSuccess) {
      setToastConfig({
        message: "Successfully saved Award Approvers routing.",
        type: "success",
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate("success");
    }
  }, [createResult]);

  useEffect(() => {
    if (resetResult.isUninitialized || resetResult.isLoading) return;
    if (resetResult.isError) {
      setToastConfig({
        message: "Failed to reset Award Approvers routing. Please try again.",
        type: "error",
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate("fail");
    }

    if (resetResult.isSuccess) {
      setToastConfig({
        message: "Successfully saved Award Approvers routing.",
        type: "success",
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate("success");
    }
  }, [resetResult]);

  const hatvAdmin = userDetailsRes?.body.find(
    (u) => u.nativeId === data?.hatvAdmin?.userNativeId
  );

  const isReset =
    data && data.award?.finalApprovalStatus !== HATV_AWARD_ROUTING_STATUS.READY;

  const isAuthorizedToAct = isReset
    ? routingRoleIndicators.isAuthorizedUpdateResetRouting
    : routingRoleIndicators.isAuthorizedCreateRouting;

  useEffect(() => {
    if (isReset) {
      setViewOnly(!isAuthorizedToAct || !localReset);
    } else {
      setViewOnly(!isAuthorizedToAct);
    }
  }, [
    routingRoleIndicators,
    isReset,
    setViewOnly,
    isAuthorizedToAct,
    localReset,
  ]);

  const handleClickRoute = async () => {
    if (!data) return;

    if (isReset && !localReset) {
      setLocalReset(true);
      setApprovers([]);
      return;
    }
    const payload = {
      performanceYear: +performanceYear,
      awardCode: aboDetails.awardCode,
      awardApprovers: approvers.map((appr, index) => ({
        approvalSequence: index + 1,
        userNativeId: appr.id,
      })),
    };

    if (isReset) {
      await resetAwardRouting({
        aboNumber: aboDetails.aboNumber,
        affiliateCode: aboDetails.affiliateCode,
        payload: {
          ...payload,
          aboHatvAwardId: data.award?.aboHatvAwardId,
          awardQualifiedMonth: aboDetails?.awardQualifiedMonth,
          awardQualifiedYear: performanceYear,
        },
      });
    } else {
      await createAwardRouting({
        aboNumber: aboDetails?.aboNumber,
        affiliateCode: aboDetails?.affiliateCode,
        payload: {
          ...payload,
          awardTagCode: aboDetails?.awardTagCode,
          awardQualifiedMonth: aboDetails?.awardQualifiedMonth,
          awardQualifiedYear: performanceYear,
        },
      });
    }
  };

  const onModalClose = () => {
    setOpen(false);
    resetApproversList();
    setLocalReset(false);
  };

  const routingStatus = data?.award
    ? t(`hatv_view_routing.routing_status.${data.award.finalApprovalStatus}`)
    : null;

  const isLoading =
    isFetching ||
    isUserListLoading ||
    createResult.isLoading ||
    resetResult.isLoading;

  return (
    <>
      <button className={styles.action_btn} onClick={() => setOpen(true)}>
        {children}
      </button>
      <Modal
        showDrawer={open}
        onClose={onModalClose}
        customStyle={ViewRoutingModalStyles}
        customContainerStyle={ViewRoutingModalContainerStyles}
      >
        <div className={`view_routing_modal ${styles["modal"]}`}>
          {isLoading ? (
            <ViewRoutingLoader />
          ) : (
            <>
              <header>
                <h1>{t("hatv_view_routing.heading")}</h1>
                <div className={`routing-info ${styles["routing-info"]}`}>
                  <div
                    className={`info-item ${styles["info-item"]} ${styles.longText}`}
                  >
                    <h2>{data?.name || aboDetails.name}</h2>
                    <p>{data?.aboNumber || aboDetails.aboNumber}</p>
                  </div>
                  <div className={`info-item ${styles["info-item"]}`}>
                    <h2>{t("hatv_view_routing.subheading_award")}:</h2>
                    <p>{data?.award?.awardName || aboDetails.awardName}</p>
                  </div>
                  <div className={`info-item ${styles["info-item"]}`}>
                    <h2>
                      {t("hatv_view_routing.subheading_approval_status")}:
                    </h2>
                    <p>{routingStatus || "-"}</p>
                  </div>
                  <div className={`info-item ${styles["info-item"]}`}>
                    <h2>{t("hatv_view_routing.subheading_hatv_admin")}:</h2>
                    <p>{hatvAdmin?.fullName || "-"}</p>
                  </div>
                </div>
              </header>
              <div>
                <ViewRoutingTable />
                {!viewOnly && <AddApproverCell disabled={isError} />}
              </div>
              {isAuthorizedToAct && (
                <footer>
                  <Button
                    id="cancelButton"
                    onClick={onModalClose}
                    label={t("hatv_view_routing.btnCancel")}
                    style="unfilled"
                  />
                  <Button
                    id="routeButton"
                    onClick={handleClickRoute}
                    label={
                      isReset && !localReset
                        ? t("hatv_view_routing.btnReset")
                        : t("hatv_view_routing.btnRoute")
                    }
                    style="filled"
                    isDisabled={
                      createResult.isLoading ||
                      resetResult.isLoading ||
                      approvers.length <= 0
                    }
                  />
                </footer>
              )}
            </>
          )}
        </div>
      </Modal>
      {/* <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: "64px",
        }}
        autoDismissal
      /> */}
    </>
  );
};

const ViewRoutingModalWrapper: React.FC<
  PropsWithChildren<ViewRoutingModalProps>
> = ({ children, ...otherProps }) => {
  return (
    <ViewRoutingProvider aboDetails={otherProps.aboDetails}>
      <ViewRoutingModal {...otherProps}>{children}</ViewRoutingModal>
    </ViewRoutingProvider>
  );
};

const getNativeIdsFromRes = (data: HATVAwardRoutingDetails) => {
  const nativeIds = new Set<string>();

  if (data?.accountManager?.userNativeId)
    nativeIds.add(data.accountManager.userNativeId);

  if (data?.hatvAdmin?.userNativeId) nativeIds.add(data.hatvAdmin.userNativeId);

  data?.award?.awardApprovers?.forEach((approver) => {
    if (approver.userNativeId) nativeIds.add(approver.userNativeId);
  });
  return Array.from(nativeIds);
};

const mapAwardApproverToApproverItem = (
  awardApprover: AwardApprover,
  usersList: UserItem[]
): Approver => {
  const userDetails = usersList.find(
    (u) => u.nativeId === awardApprover.userNativeId
  );
  return {
    date: awardApprover.approvalDate,
    deleted: false,
    designation: "TBD",
    name: userDetails?.fullName || "-",
    id: awardApprover.userNativeId,
    status: awardApprover.approvalStatus,
  };
};

export default ViewRoutingModalWrapper;
